import { FC, useCallback } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';
import { Sizes } from 'enums/sizes';
import { getSizeValue } from 'helpers/getSizesValue';
import DialogHeader from '../../Dialog/DialogHeader';
import DialogBody from '../../Dialog/DialogBody';
import DialogFooter from '../../Dialog/DialogFooter';
import { BasicDialogConfig, DialogActions, DialogSessionContext, OverlaySession } from '../OverlayContext';
import { Z_INDEX } from 'shared/constants/zIndex';

const sizes = {
  [Sizes.xs]: 'sm:max-w-xs',
  [Sizes.sm]: 'sm:max-w-sm',
  [Sizes.md]: 'sm:max-w-md',
  [Sizes.lg]: 'sm:max-w-lg',
  [Sizes.xl]: 'sm:max-w-xl',
  [Sizes['2xl']]: 'sm:max-w-2xl',
  [Sizes['3xl']]: 'sm:max-w-3xl',
  [Sizes['4xl']]: 'sm:max-w-4xl',
  [Sizes['5xl']]: 'sm:max-w-5xl',
};

interface DialogRendererProps {
  sessions: OverlaySession[];
  closeDialog: () => void;
  onTransitionEnd: (id: string) => void;
}

export const DialogRenderer: FC<DialogRendererProps> = ({ sessions, closeDialog, onTransitionEnd }) => {
  const lastActiveSession = sessions.filter(s => !s.closing).pop();

  return (
    <>
      {sessions.map((session, index) => (
        <DialogItem
          key={session.id}
          session={session}
          index={index}
          isLast={session === lastActiveSession}
          closeDialog={closeDialog}
          onTransitionEnd={onTransitionEnd}
        />
      ))}
    </>
  );
};

interface DialogItemProps {
  session: OverlaySession;
  index: number;
  isLast: boolean;
  closeDialog: () => void;
  onTransitionEnd: (id: string) => void;
}

const DialogItem: FC<DialogItemProps> = ({ session, index, isLast, closeDialog, onTransitionEnd }) => {
  const config = session.config as BasicDialogConfig;
  const actions = session.actions as DialogActions | undefined;
  const isBlank = session.type === 'blank-dialog';
  const sizeValue = getSizeValue(config.size, sizes);
  const zIndex = Z_INDEX.DIALOG_BASE + index * Z_INDEX.DIALOG_STEP;

  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (session.closing && e.target === e.currentTarget && e.propertyName === 'transform') {
      onTransitionEnd(session.id);
    }
  }, [session.closing, session.id, onTransitionEnd]);

  if (isBlank) {
    return (
      <Dialog
        open={!session.closing}
        onClose={isLast ? closeDialog : () => {}}
        className="relative"
        style={{ zIndex }}
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16"
          style={{ zIndex }}
        />
        <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0" style={{ zIndex: zIndex + 1 }}>
          <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
            <DialogPanel
              transition
              className={clsx(
                sizeValue,
                'row-start-2 w-full min-w-0 rounded-t-3xl bg-white overflow-hidden shadow-lg ring-1 ring-zinc-950/10 [--gutter:theme(spacing.8)] sm:mb-auto sm:rounded-2xl forced-colors:outline',
                'transition duration-100 will-change-transform data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:data-[closed]:translate-y-0 sm:data-[closed]:data-[enter]:scale-95'
              )}
              onTransitionEnd={handleTransitionEnd}
            >
              <DialogSessionContext.Provider value={session.id}>
                {session.content}
              </DialogSessionContext.Provider>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={!session.closing}
      onClose={isLast ? closeDialog : () => {}}
      className="relative"
      style={{ zIndex }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16"
        style={{ zIndex }}
      />
      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0" style={{ zIndex: zIndex + 1 }}>
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <DialogPanel
            transition
            className={clsx(
              sizeValue,
              'row-start-2 w-full min-w-0 rounded-t-3xl bg-white overflow-hidden p-[--gutter] shadow-lg ring-1 ring-zinc-950/10 [--gutter:theme(spacing.8)] sm:mb-auto sm:rounded-2xl forced-colors:outline',
              'transition duration-100 will-change-transform data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:data-[closed]:translate-y-0 sm:data-[closed]:data-[enter]:scale-95'
            )}
            onTransitionEnd={handleTransitionEnd}
          >
            <DialogHeader title={config.title} description={config.description} onClose={closeDialog} />
            <DialogBody isLoading={false}>
              <DialogSessionContext.Provider value={session.id}>
                {session.content}
              </DialogSessionContext.Provider>
            </DialogBody>
            {actions && (
              <DialogFooter {...actions} isLoading={false} onClose={closeDialog} />
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
